import { ReferralTier } from '../types/';

import { CircularProgressSvg } from './CircularProgressSvg';

type CircularProgressProps = {
  progress: number;
  currentRewards: number;
  totalRewards: number;
  title: string;
  nextRewardName: string;
  tiers: ReferralTier[];
};

export const CircularProgress = ({
  progress,
  currentRewards,
  totalRewards,
  title,
  nextRewardName,
  tiers,
}: CircularProgressProps) => {
  const clampedProgress = Math.min(Math.max(progress, 0), 100);
  const totalTiles = 16;
  const outerRadius = 120; // visual size on mobile
  const baseInnerRadius = 90; // base ring thickness = outer - baseInner
  // reduce tile height (ring thickness) by 20%
  const thickness = (outerRadius - baseInnerRadius) * 0.6;
  const innerRadius = outerRadius - thickness;
  const gapDegrees = 7; // space between tiles

  // Calculate which tiles should show labels based on tiers with equal spacing
  // Skip the last tier to avoid overlap at the start/end of the circle
  const tiersToShow = tiers.slice(0, -1);
  const labeledTiles = tiersToShow.map((tier, index) => {
    // Distribute tiers evenly around the circle (16 tiles)
    const tileIndex = Math.round((index * totalTiles) / tiersToShow.length);
    return {
      index: tileIndex,
      label: tier.requiredRewards.toString(),
    };
  });

  // Color palette provided (per tile, starting from the first at top)
  const TILE_COLORS: string[] = [
    '#3154f9',
    '#3159ee',
    '#305ee2',
    '#2f62d8',
    '#2e67cd',
    '#2d6cc3',
    '#2c71b8',
    '#2b75af',
    '#2a7aa4',
    '#2a809a',
    '#2a809a',
    '#298986',
    '#298f7d',
    '#289373',
    '#27986a',
    '#279e61',
  ];

  // Base (inactive) color controlled in SVG; this fallback not used when tileStateProvider is passed
  const getTileColor = () => '#314272';

  // provide partial-fill per tile based on precise progress
  const filledTilesFloat = (clampedProgress / 100) * totalTiles;
  const tileStateProvider = (index: number) => {
    const ratio = Math.max(0, Math.min(1, filledTilesFloat - index));
    const color = TILE_COLORS[index % TILE_COLORS.length] ?? '#2D5EFF';
    return { ratio, color };
  };

  return (
    <div className="tw-relative tw-w-full lg:tw-hidden" style={{ aspectRatio: '1 / 1' }}>
      <CircularProgressSvg
        totalTiles={totalTiles}
        outerRadius={outerRadius}
        innerRadius={innerRadius}
        gapDegrees={gapDegrees}
        labels={labeledTiles}
        getTileColor={getTileColor}
        tileStateProvider={tileStateProvider}
        className="tw-absolute tw-inset-0"
      />
      <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-[10px] tw-font-jost tw-text-white">
        <span className="tw-text-[32px] tw-font-bold">{title}</span>
        <span className="tw-text-[16px] tw-font-medium">
          {currentRewards}/{totalRewards}
        </span>
        <div className="tw-flex tw-flex-col">
          <span className="tw-text-[16px] tw-font-medium">Next Reward:</span>
          <span className="tw-text-[16px] tw-font-medium">{nextRewardName}</span>
        </div>
      </div>
    </div>
  );
};
